import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";
import { RootState } from "../../app/store";
import {
  EezyCompanyFilters,
  getPaymentTerms,
  InvoiceLanguage,
  PaymentTerms,
} from "../invoices/invoiceAPI";
import {
  getSalesAccountConfigForDate,
  SalesAccountConfigFilters,
  SalesAccounts,
} from "../settings/settingsApi";
import {
  ContractEvent,
  ContractPricingEvent,
  ContractPricingUpdatePost,
  ContractUpdatePost,
  CustomerContract,
  getContractById,
  getCustomerOrgContracts,
  runCreateCustomerContract,
  TesExpenseTypeCode,
  tesExpenseTypeCodesQuery,
  TesExpenseTypeCodesQueryFilters,
  updateCustomerContract,
  updateCustomerContractPricing,
} from "./customerAPI";
import { CreateCustomerContract } from "./types/commands";
import { CustomerContractCreated } from "./types/events";

type ContractResult = any;
// TODO ContractResult should be CustomerContract. Need to update CustomerContract properties.

const customerContractsAdapter = createEntityAdapter<ContractResult>();

type SliceState = {
  ids: [];
  entities: Record<string, any>;
  status: "idle" | "pending" | "succeeded" | "failed";
};

export const fetchCustomerOrgContracts = createAsyncThunk<
  CustomerContract[],
  string,
  { state: RootState }
>(
  "customerContracts/fetchCustomerOrgContracts",
  async (customerOrgId: string, { requestId }) => {
    const response = await getCustomerOrgContracts(customerOrgId, {
      requestId,
    });
    return response.data;
  },
);

export const fetchContractById = createQueryAsyncThunk<
  any,
  { contractId: string }
>("customerContracts/fetchContractById", getContractById);

export const fetchPaymentTerms = createAsyncThunk<
  PaymentTerms,
  EezyCompanyFilters,
  { state: RootState }
>(
  "customerContracts/fetchPaymentTerms",
  async (filters: EezyCompanyFilters, { requestId }) => {
    const response = await getPaymentTerms(filters, { requestId });
    return response;
  },
);

export const fetchSalesAccounts = createAsyncThunk<
  SalesAccounts,
  SalesAccountConfigFilters,
  { state: RootState }
>(
  "customerContracts/fetchSalesAccounts",
  async ({ validFrom }: SalesAccountConfigFilters) => {
    const response = await getSalesAccountConfigForDate({ validFrom });
    return response.data;
  },
);

export const updateContract = createAsyncThunk<
  ContractEvent[],
  ContractUpdatePost,
  { state: RootState }
>(
  "customerContracts/updateContract",
  async (contract: ContractUpdatePost, { requestId }) => {
    const response = await updateCustomerContract(contract, { requestId });
    return response.data;
  },
);

export const updateContractPricing = createAsyncThunk<
  ContractPricingEvent[],
  ContractPricingUpdatePost,
  { state: RootState }
>(
  "customerContracts/updateContractPricing",
  async (pricing: ContractPricingUpdatePost, { requestId }) => {
    const response = await updateCustomerContractPricing(pricing, {
      requestId,
    });
    return response.data;
  },
);

export const fetchTesExpenseTypeCodes = createAsyncThunk<
  TesExpenseTypeCode[],
  TesExpenseTypeCodesQueryFilters,
  { state: RootState }
>(
  "customerContracts/fetchTesExpenseTypeCodes",
  async (filters: TesExpenseTypeCodesQueryFilters, { requestId }) => {
    const response = await tesExpenseTypeCodesQuery(filters, { requestId });
    return response;
  },
);

export const createCustomerContract = createCommandAsyncThunk<
  CreateCustomerContract,
  CustomerContractCreated
>("customerOrganizations/createCustomerContract", runCreateCustomerContract);

const customerContractsSlice = createSlice({
  name: "customerContractsSlice",
  initialState: customerContractsAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {
    resetCustomerContracts: (state) => {
      customerContractsAdapter.removeAll(state);
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCustomerOrgContracts.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerOrgContracts.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCustomerOrgContracts.fulfilled, (state, action) => {
      customerContractsAdapter.setAll(state, action.payload);
      state.status = "succeeded";
    });
    builder.addCase(updateContract.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateContract.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateContract.fulfilled, (state, action) => {
      const { id: contractId } = action.meta.arg;
      const contract = state.entities[contractId] as CustomerContract;

      action.payload.forEach((event) => {
        const version = event.version as number;
        switch (event.name) {
          // TODO add new cases when BE supports new properties
          case "cntValidFromChg":
            contract.validFrom = event.payload.validFrom;
            contract.version = version;
            break;
          case "cntSlpChg":
            contract.salesPersonId = event.payload.salesPersonId;
            contract.salesPersonName = action.meta.arg.salesPersonName;
            contract.version = version;
            break;
          case "cntOrdQueChg":
            contract.orderQueueId = event.payload.orderQueueId;
            contract.version = version;
            break;
          case "cntResPerChg":
            contract.contractResponsiblePerson = event.payload.contactPerson;
            contract.version = version;
            break;
          case "cntInvResPerChg":
            contract.invoicingResponsiblePerson = event.payload.contactPerson;
            contract.version = version;
            break;
          case "cntPmtChg":
            contract.paymentTermDays = event.payload.paymentTermDays;
            contract.version = version;
            break;
          case "cntInvByCcChg":
            contract.invoicesByCostCenter = event.payload.invoicesByCostCenter;
            contract.version = version;
            break;
          case "cntInvfChg":
            contract.invoiceFormat = event.payload.invoiceFormat;
            contract.version = version;
            break;
          case "cntInvbChg":
            contract.invoiceBreakdown = event.payload.invoiceBreakdown;
            contract.version = version;
            break;
          case "cntSacChg":
            contract.salesAccount = {
              ...contract.salesAccount,
              code: event.payload.salesAccount,
            };
            contract.version = version;
            break;
          case "cntInvrChg":
            contract.invoiceReferences = event.payload.invoiceReferences;
            contract.version = version;
            break;
          case "cntOrgsChg":
            contract.customerOrgId = event.payload.customerOrgId;
            contract.version = version;
            break;
          case "cntFacChg":
            contract.factoring = event.payload.factoring;
            contract.version = version;
            break;
          case "cntInvByWrlChg":
            contract.invoicesByWorkLocation =
              event.payload.invoicesByWorkLocation;
            contract.version = version;
            break;
          case "cntInvDlvdOn":
            contract.invoiceDeliveryDate = true;
            contract.version = version;
            break;
          case "cntInvDlvdOff":
            contract.invoiceDeliveryDate = false;
            contract.version = version;
            break;
          case "cntInvLngChg":
            contract.invoiceLanguage = event.payload.invoiceLanguage;
            contract.version = version;
            break;
          default:
            break;
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(updateContractPricing.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateContractPricing.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateContractPricing.fulfilled, (state, action) => {
      const { id: contractId } = action.meta.arg;
      const contract = state.entities[contractId] as CustomerContract;

      action.payload.forEach((event) => {
        const version = event.version as number;
        switch (event.name) {
          case "cntPrcChg":
            // If backDate is given, it means that the pricing is being updated for a new date
            // so the old record should be removed
            if (action.meta.arg.backDate) {
              delete contract.pricing[action.meta.arg.validFrom];
            }
            contract.version = version;
            contract.pricing = {
              ...contract.pricing,
              [event.payload.validFrom]: {
                ...event.payload.pricing,
              },
            };
            break;
          default:
            break;
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(createCustomerContract.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createCustomerContract.fulfilled, (state, action) => {
      state.status = "succeeded";
      const cntAdded = action.payload.find(
        (event) => event.name === "cntAdd",
      )!!;
      const highestVersion = action.payload.reduce(
        (max, event) => Math.max(max, event.version),
        0,
      );
      const result: ContractResult = {
        id: cntAdded.id,
        customerCompanyId: cntAdded.payload.customerCompanyId,
        name: cntAdded.payload.name,
        version: highestVersion,
        salesPersonId: cntAdded.payload.salesPersonId,
        invoiceDeliveryDate: false,
        invoiceLanguage: InvoiceLanguage.FI,
        eezyCompany: {
          id: cntAdded.payload.eezyCompanyId,
          name: action.meta.arg.eezyCompanyName,
          businessId: action.meta.arg.eezyCompanyBusinessId,
        },
      };
      customerContractsAdapter.addOne(state, result);
    });
    builder.addCase(createCustomerContract.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchContractById.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchContractById.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchContractById.fulfilled, (state, action) => {
      // TODO YLPE-8547: contractById returns a flat `eezyCompanyId` and no
      // nested `eezyCompany` object, unlike contractsByCustomerOrgId. This upserts
      // an entity with a different shape into the shared customerContracts slice,
      // which other consumers (e.g. CustomerManagement) assume always has `eezyCompany`.
      const result: ContractResult = {
        ...action.payload,
        id: action.payload.contractId,
      };
      customerContractsAdapter.upsertOne(state, result);
      state.status = "succeeded";
    });
  },
});

export const { resetCustomerContracts } = customerContractsSlice.actions;

export const { selectAll: selectAllCustomerContracts } =
  customerContractsAdapter.getSelectors(
    (state: RootState) => state.customerContracts,
  );

export const customerContractsReducer = customerContractsSlice.reducer;
